// pages/Offers.jsx
import React, { useState } from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import GiftHeroBit from '../components/gifts/GiftHeroBit';
import GiftGridBit from '../components/gifts/GiftGridBit';
import LuxurySetBanner from '../components/gifts/LuxurySetBanner';

const OffersPage = () => {
    const [activeTab, setActiveTab] = useState('sets');

    const offers = [
        {
            id: 1,
            name: "The Signature Ritual Set",
            price: 285,
            originalPrice: 340,
            discount: "Save 16%",
            description: "Our complete skincare system featuring full-size versions of our best-selling products.",
            image: "/images/offer-set-1.jpg",
            tags: ["Best Seller", "Complete System", "Limited Time"],
            occasion: ["selfcare", "premium"],
            isFeatured: true
        },
        {
            id: 2,
            name: "Duo Discovery Pack",
            price: 145,
            originalPrice: 180,
            discount: "Save 19%",
            description: "Perfect for trying our hero products: Supreme Serum + Dew Cream.",
            image: "/images/offer-set-2.jpg",
            tags: ["New", "Trial Size", "Perfect Pair"],
            occasion: ["birthday", "selfcare"],
            isFeatured: false
        },
        {
            id: 3,
            name: "Travel Ritual Collection",
            price: 98,
            originalPrice: 120,
            discount: "Save 18%",
            description: "All your essentials in travel-friendly sizes, perfect for on-the-go luxury.",
            image: "/images/offer-set-3.jpg",
            tags: ["Travel", "Mini Set", "Essential"],
            occasion: ["travel", "selfcare"],
            isFeatured: true
        },
        {
            id: 4,
            name: "Gifting Trio",
            price: 210,
            originalPrice: 255,
            discount: "Save 18%",
            description: "Three perfectly curated products for the ultimate gift experience.",
            image: "/images/offer-set-4.jpg",
            tags: ["Gift Ready", "Triple Value", "Festive"],
            occasion: ["festive", "birthday"],
            isFeatured: false
        },
        {
            id: 5,
            name: "Luxe Night Ritual",
            price: 320,
            originalPrice: 380,
            discount: "Save 16%",
            description: "Elevate your evening routine with our most luxurious overnight treatments.",
            image: "/images/offer-set-5.jpg",
            tags: ["Overnight", "Luxury", "Regenerative"],
            occasion: ["premium", "selfcare"],
            isFeatured: true
        },
        {
            id: 6,
            name: "Morning Glow Essentials",
            price: 175,
            originalPrice: 210,
            discount: "Save 17%",
            description: "Wake up to radiant skin with this energizing morning collection.",
            image: "/images/offer-set-6.jpg",
            tags: ["Morning", "Glow", "Daily"],
            occasion: ["selfcare", "travel"],
            isFeatured: false
        }
    ];

    const seasonalOffers = [
        {
            id: 7,
            name: "Holiday Limited Edition",
            price: 450,
            originalPrice: 520,
            discount: "Save 13%",
            description: "Exclusive holiday packaging with our full collection, plus a limited edition candle.",
            image: "/images/seasonal-1.jpg",
            tags: ["Limited", "Holiday", "Exclusive"],
            occasion: ["festive", "premium"],
            isLimited: true
        },
        {
            id: 8,
            name: "Spring Renewal Set",
            price: 195,
            originalPrice: 230,
            discount: "Save 15%",
            description: "Refresh your routine with our lightweight spring formulations.",
            image: "/images/seasonal-2.jpg",
            tags: ["Seasonal", "Lightweight", "Fresh"],
            occasion: ["selfcare", "birthday"],
            isLimited: true
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
        >
            <GiftHeroBit
                title="Offers & Sets"
                subtitle="Thoughtfully curated collections with exceptional value"
                tagline="Experience more for less with our specially priced sets"
            />

            <LuxurySetBanner />

            {/* Tab Navigation */}
            <section className="py-12 bg-ivory">
                <div className="container mx-auto px-6 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="flex justify-center"
                    >
                        <div className="inline-flex rounded-full bg-white p-1 shadow-soft">
                            {['sets', 'seasonal', 'gifting'].map((tab) => (
                                <motion.button
                                    key={tab}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-8 py-3 rounded-full font-poppins transition-all duration-300 ${
                                        activeTab === tab
                                            ? 'bg-sage-green text-white'
                                            : 'text-charcoal hover:text-sage-green'
                                    }`}
                                >
                                    {tab === 'sets' && 'Best Value Sets'}
                                    {tab === 'seasonal' && 'Seasonal Offers'}
                                    {tab === 'gifting' && 'Gift Collections'}
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Offers Grid */}
            <section className="py-16 bg-ivory">
                <div className="container mx-auto px-6 lg:px-12">
                    <AnimatePresence mode="wait">
                        {activeTab === 'sets' && (
                            <motion.div
                                key="sets"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-12 text-center">
                                    Best Value Sets
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {offers.map((offer, index) => (
                                        <motion.div
                                            key={offer.id}
                                            initial={{ opacity: 0, y: 40 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: index * 0.1 }}
                                            whileHover={{ y: -12 }}
                                            className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-luxe"
                                        >
                                            <div className="relative h-64 overflow-hidden">
                                                <motion.img
                                                    whileHover={{ scale: 1.05 }}
                                                    transition={{ duration: 0.6 }}
                                                    src={offer.image}
                                                    alt={offer.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-4 right-4">
                          <span className="bg-champagne text-charcoal px-3 py-1 rounded-full text-sm font-poppins">
                            {offer.discount}
                          </span>
                                                </div>
                                                {offer.isFeatured && (
                                                    <div className="absolute top-4 left-4 bg-sage-green text-white px-3 py-1 rounded-full text-sm font-poppins">
                                                        Featured
                                                    </div>
                                                )}
                                            </div>

                                            <div className="p-6">
                                                <div className="flex justify-between items-start mb-4">
                                                    <h3 className="font-cormorant text-2xl text-charcoal">{offer.name}</h3>
                                                    <div className="text-right">
                                                        <div className="font-playfair text-2xl text-sage-green">${offer.price}</div>
                                                        <div className="font-poppins text-sm text-charcoal/50 line-through">
                                                            ${offer.originalPrice}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="font-poppins text-charcoal/70 mb-6">{offer.description}</p>

                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {offer.tags.map((tag, tagIndex) => (
                                                        <span
                                                            key={tagIndex}
                                                            className="px-3 py-1 bg-ivory text-charcoal/70 rounded-full text-sm font-poppins"
                                                        >
                              {tag}
                            </span>
                                                    ))}
                                                </div>

                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="w-full py-3 bg-charcoal text-white font-poppins rounded-lg
                            hover:bg-charcoal/90 transition-all duration-300"
                                                >
                                                    Add to Cart
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'seasonal' && (
                            <motion.div
                                key="seasonal"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-12 text-center">
                                    Seasonal & Limited Offers
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {seasonalOffers.map((offer, index) => (
                                        <motion.div
                                            key={offer.id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.6, delay: index * 0.2 }}
                                            whileHover={{ y: -8 }}
                                            className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-luxe"
                                        >
                                            <div className="relative h-80 overflow-hidden">
                                                <motion.img
                                                    whileHover={{ scale: 1.05 }}
                                                    transition={{ duration: 0.6 }}
                                                    src={offer.image}
                                                    alt={offer.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                                <div className="absolute bottom-6 left-6 right-6">
                                                    <div className="flex justify-between items-end">
                                                        <div>
                                                            <h3 className="font-cormorant text-3xl text-white mb-2">{offer.name}</h3>
                                                            <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-poppins">
                                {offer.discount}
                              </span>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="font-playfair text-3xl text-white">${offer.price}</div>
                                                            <div className="font-poppins text-white/70 line-through">
                                                                ${offer.originalPrice}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-6">
                                                <p className="font-poppins text-charcoal/70 mb-6">{offer.description}</p>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex flex-wrap gap-2">
                                                        {offer.tags.map((tag, tagIndex) => (
                                                            <span
                                                                key={tagIndex}
                                                                className="px-3 py-1 bg-ivory text-charcoal/70 rounded-full text-sm font-poppins"
                                                            >
                                {tag}
                              </span>
                                                        ))}
                                                    </div>
                                                    {offer.isLimited && (
                                                        <div className="text-sage-green font-poppins text-sm">
                                                            ⏰ Limited Time Only
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'gifting' && (
                            <motion.div
                                key="gifting"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-12 text-center">
                                    Perfect Gifting Solutions
                                </h2>
                                <div className="max-w-6xl mx-auto">
                                    <GiftGridBit giftSets={offers.filter(o => o.id <= 4)} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* Value Proposition Section */}
            <section className="py-24 bg-nude-beige">
                <div className="container mx-auto px-6 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <h2 className="font-playfair text-5xl md:text-6xl text-charcoal mb-8">
                            Why Choose KROMAIC Sets?
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                            {[
                                {
                                    icon: "💰",
                                    title: "Exceptional Value",
                                    description: "Save up to 20% compared to purchasing items individually"
                                },
                                {
                                    icon: "🎁",
                                    title: "Perfectly Paired",
                                    description: "Products curated by our experts for optimal results"
                                },
                                {
                                    icon: "✨",
                                    title: "Complete Rituals",
                                    description: "Everything you need for a full skincare experience"
                                }
                            ].map((benefit, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    className="p-6"
                                >
                                    <div className="text-4xl mb-4">{benefit.icon}</div>
                                    <h3 className="font-cormorant text-2xl text-charcoal mb-4">
                                        {benefit.title}
                                    </h3>
                                    <p className="font-poppins text-charcoal/70">
                                        {benefit.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="mt-16"
                        >
                            <p className="font-poppins text-xl text-charcoal/70 mb-8">
                                Still unsure which set is right for you?
                            </p>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                                className="px-12 py-4 bg-sage-green text-white font-poppins text-lg rounded-full
                  hover:bg-sage-green/90 transition-all duration-300 shadow-soft"
                            >
                                Take Our Skincare Quiz
                            </motion.button>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    );
};



export default OffersPage;